import { Grid, Input, Slider, Typography } from '@mui/material';
import { map } from 'lodash';
import React, { useId } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

export interface SliderInputWithLabelProps<FormValues extends FieldValues = FieldValues> {
  label: string;
  fieldName: string;
  control: Control<FormValues>;
  min: number;
  max: number;
  step: number;
  onValueChange?: () => void;
}

export const SliderInputWithLabel = <FormValues extends FieldValues>({
  label,
  control,
  fieldName,
  min,
  max,
  step,
  onValueChange,
}: SliderInputWithLabelProps<FormValues>) => {
  const labelId = useId();
  return (
    <Grid item container direction="row" spacing={2} alignItems="center">
      <Grid item>
        <Typography variant="body2" id={labelId}>
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={fieldName as Path<FormValues>}
          render={({ field: { onChange, value } }) => (
            <Slider
              sx={{ width: 200 }}
              value={Number(value)}
              onChange={(_event, nextValue) => {
                onChange(nextValue);
                onValueChange?.();
              }}
              valueLabelDisplay="auto"
              min={min}
              max={max}
              step={step}
              aria-labelledby={labelId}
            />
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={fieldName as Path<FormValues>}
          render={({ field: { onChange, value } }) => (
            <Input
              value={value}
              size="small"
              onChange={(event) => {
                onChange(event);
                onValueChange?.();
              }}
              onBlur={() => {
                if (Number(value) < min) onChange(min);
                if (Number(value) > max) onChange(max);
                onValueChange?.();
              }}
              inputProps={{ step, min, max, type: 'number', 'aria-labelledby': labelId }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

interface Props<FormValues extends FieldValues> {
  control: Control<FormValues>;
  fieldPrefix?: string;
  labelPrefix?: string;
  onValueChange?: () => void;
}

export const BinaryClassifierThresholdFlowParameters = <FormValues extends FieldValues>({
  control,
  fieldPrefix = 'configParams',
  labelPrefix = '',
  onValueChange,
}: Props<FormValues>) => (
  <>
    {map(
      [
        { key: 'rangeStart', label: 'Range Start', max: 1, step: 0.01 },
        { key: 'rangeEnd', label: 'Range End', max: 1, step: 0.01 },
        { key: 'maxThreshold', label: 'Max Threshold', max: 1, step: 0.01 },
        { key: 'precisionDecimal', label: 'Precision Decimal', max: 10, step: 1 },
      ],
      (parameter) => (
        <SliderInputWithLabel
          key={parameter.key}
          label={labelPrefix ? `${labelPrefix} ${parameter.label}` : parameter.label}
          fieldName={`${fieldPrefix}.${parameter.key}`}
          control={control}
          min={0}
          max={parameter.max}
          step={parameter.step}
          onValueChange={onValueChange}
        />
      )
    )}
  </>
);
